/* ==========================================================================
   KITSTERS - MAGIC HOUR CART GUARD (sitewide)
   --------------------------------------------------------------------------
   Owner 2026-08-19: "still issue is that in normal cart also, magic hour
   product is added. So fix that issue."

   WHY MAGIC KITS EVER TOUCH THE REAL CART AT ALL: Shopify's checkout can
   only sell what is in the cart, so the Magic Hour hand-off (the MH PDP's
   express path and the magic cart's checkout - the only two writers) has to
   clear the cart, add the kits, and navigate to /checkout. Complete the
   payment and Shopify empties the cart itself. ABANDON it - back button,
   closed tab - and the kits are still sitting in the real cart, so the
   Kalles drawer, the header badge and /cart all show them. That is the
   report above.

   THE FIX: all three hand-offs (the two above, and since 2026-09-16 the
   Experience page's booking flow) stamp localStorage `kt_magic_handoff`
   immediately before navigating. This guard runs on EVERY page (theme.liquid), and when
   - and only when - that stamp exists, it reads /cart.js and removes every
   line whose vendor is the hidden Magic Hour vendor, then clears the
   hand-off's attributes + note. No stamp -> not one network request, so the
   other ~130 pages pay nothing for it (the stamp can only exist after a
   hand-off, and only ever briefly).

   Why gate on a stamp instead of sweeping unconditionally: an unconditional
   sweep is a /cart.js GET on every page view sitewide forever, for a state
   that can only exist after a Magic Hour hand-off. The stamp makes the
   common case free and the interesting case exact (§10.90's "group the
   bytes before optimising" instinct, applied to requests).

   The vendor comes from the kt-hidden-vendor META that layout/theme.liquid
   already publishes on every page (settings.kt_hidden_vendor - the same
   single source of truth the catalogue-hiding sweep uses), with the literal
   as fallback so a blanked setting cannot turn the guard off silently.

   Line removal uses ONE /cart/update.js call with the lines' KEYS (quantity
   0). Keys, not variant ids, and as strings - documented Ajax Cart API
   behaviour (re-checked 2026-08-19): a cart can hold several lines of one
   variant when properties differ, and the key targets exactly the intended
   line.

   Edge worth stating: if the shopper abandoned checkout and then added
   RETAIL items before this guard ran, only the hand-off lines are removed
   and the retail lines survive. It is never /cart/clear.js: measured
   2026-09-16, clear.js empties the lines only and leaves the attributes and
   the note in place, so the blanking below is the only thing that actually
   removes them (§10.106).
   ========================================================================== */
(function () {
  'use strict';

  var FLAG = 'kt_magic_handoff';

  /* The attribute keys the hand-offs write - cleared here so an abandoned
     Magic Hour checkout can never leak "Company name" / "Order type: Magic
     Hour" attributes onto a later RETAIL order. Keep in step with
     orderAttributes() in kitsters-magic-cart.js / kitsters-magic-hour-
     product.js. */
  var HANDOFF_ATTRS = [
    'Company name', 'GST number', 'Ordered by', 'Designation',
    'Contact email', 'Contact phone', 'Registered address',
    'Deliver to registered address', 'Order type', 'Hours', 'Kits',
    /* the Experience page's hand-off (kitsters-experience-product.js §3) */
    'People', 'Session length', 'Session date', 'Session time (IST)',
    'Special requests', 'Kits ship to'
  ];

  /* A hand-off line is EITHER a hidden-vendor (Magic Hour) line OR any line
     carrying the private `_kt_handoff` property - the Experience page's
     booking flow stamps that on its line (2026-09-16), because an experience
     is a RETAIL kit sold per person, not a hidden-vendor product, so the
     vendor test alone would leave an abandoned booking sitting in the real
     cart. Underscore-prefixed properties are Shopify's private-property
     convention: hidden from the shopper in cart and checkout, visible on the
     order in Admin. */
  function isHandoffLine(it, vendor) {
    if (it.vendor === vendor) return true;
    return !!(it.properties && it.properties._kt_handoff);
  }

  function readFlag() {
    try { return window.localStorage.getItem(FLAG); } catch (e) { return null; }
  }
  function clearFlag() {
    try { window.localStorage.removeItem(FLAG); } catch (e) { /* no-op */ }
  }

  function hiddenVendor() {
    var m = document.querySelector('meta[name="kt-hidden-vendor"]');
    return (m && m.getAttribute('content')) || 'Magic Hour';
  }

  function announce(cart) {
    /* Tell the real cart's UI what just changed, or the drawer/badge keep
       showing lines that are gone until a reload (§10.71). */
    if (document.querySelector('hdt-cart-drawer')) {
      document.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
    }
    document.dispatchEvent(new CustomEvent('cart:count:update', {
      detail: { item_count: cart.item_count, total_price: cart.total_price }
    }));
  }

  var sweeping = false;

  function sweep() {
    if (sweeping || !readFlag()) return;
    sweeping = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart-read-failed'); return r.json(); })
      .then(function (cart) {
        var vendor = hiddenVendor();
        var items = cart.items || [];
        var magic = items.filter(function (it) { return isHandoffLine(it, vendor); });

        if (!magic.length) {
          /* Checkout completed (Shopify emptied the cart) or nothing ever
             landed - either way the stamp's job is done. */
          clearFlag();
          sweeping = false;
          return;
        }

        /* ONE /cart/update.js call whether or not retail lines are present:
           the hand-off lines are zeroed by KEY, the hand-off's attributes are
           blanked and the note cleared. The branch that used /cart/clear.js
           when nothing but hand-off lines were present is GONE - measured
           2026-09-16 on the live store, clear.js empties the LINES ONLY and
           leaves every cart attribute and the note exactly as they were, so
           that branch left "Company name" / "Order type" / the whole billing
           note sitting on the cart to ride onto the shopper's next retail
           order. update.js with the keys set to "" removes them (measured:
           14 keys -> 0), and `note: ""` clears the note. */
        var updates = {};
        magic.forEach(function (it) { updates[String(it.key)] = 0; });
        var attrs = {};
        HANDOFF_ATTRS.forEach(function (k) { attrs[k] = ''; });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates, attributes: attrs, note: '' })
        })
          .then(function (r) { if (!r.ok) throw new Error('update-failed'); return r.json(); })
          .then(function (updated) {
            clearFlag();
            sweeping = false;
            announce(updated);
          });
      })
      .catch(function () {
        /* Network hiccup: keep the stamp so the next page view retries. */
        sweeping = false;
      });
  }

  /* Three ways back from a checkout, and a fresh load is only one of them:
     bfcache restore fires pageshow (persisted) with no load event, and a
     tab left open behind the checkout tab fires visibilitychange when the
     shopper returns to it. All three are stamp-gated, so they are free in
     the steady state. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) sweep(); });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) sweep();
  });
})();
